import { useEffect, useId, useMemo, useRef, useState } from 'react';
import '../scss/component/_tab.scss';
import { components } from '.';

export type TabItem = {
  id?: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type TabsProps = {
  tabs: TabItem[];
  index?: number;
  defaultIndex?: number;
  onChange?: (index: number) => void;
  className?: string;
  tabListClassName?: string;
  tabClassName?: (args: {
    active: boolean;
    disabled: boolean;
  }) => string | string;
  panelClassName?: string;
  autoFocusActive?: boolean;
  isLoading?: boolean;
};

export const TabView: React.FC<TabsProps> = ({
  tabs,
  index,
  defaultIndex = 0,
  onChange,
  className = '',
  tabListClassName = '',
  tabClassName,
  panelClassName = '',
  autoFocusActive = false,
  isLoading = false,
}: TabsProps) => {
  const isControlled = index !== undefined;
  const [internalIndex, setInternalIndex] = useState(defaultIndex);
  const selectedIndex = isControlled ? (index as number) : internalIndex;

  const baseId = useId();
  const tabIds = useMemo(
    () => tabs.map((t, i) => t.id ?? `${baseId}-tab-${i}`),
    [tabs, baseId]
  );
  const panelIds = useMemo(() => tabIds.map((tid) => `${tid}-panel`), [tabIds]);

  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (!autoFocusActive) return;
    const el = tabRefs.current[selectedIndex];
    if (el) el.focus();
  }, [selectedIndex, autoFocusActive]);

  const setIndex = (i: number) => {
    if (tabs[i]?.disabled) return;
    if (isControlled) {
      onChange?.(i);
    } else {
      setInternalIndex(i);
      onChange?.(i);
    }
  };

  const focusTab = (i: number) => {
    const el = tabRefs.current[i];
    if (el) el.focus();
  };

  const nextEnabledIndex = (start: number, dir: 1 | -1) => {
    const n = tabs.length;
    let i = start;
    for (let step = 0; step < n; step++) {
      i = (i + dir + n) % n;
      if (!tabs[i]?.disabled) return i;
    }
    return start;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const current = tabRefs.current.indexOf(
      document.activeElement as HTMLButtonElement
    );
    if (current === -1) return;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        e.preventDefault();
        const i = nextEnabledIndex(current, 1);
        focusTab(i);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        e.preventDefault();
        const i = nextEnabledIndex(current, -1);
        focusTab(i);
        break;
      }
      case 'Home': {
        e.preventDefault();
        const i = nextEnabledIndex(0, 1);
        focusTab(i);
        break;
      }
      case 'End': {
        e.preventDefault();
        let i = tabs.length - 1;
        while (i >= 0 && tabs[i]?.disabled) i--;
        focusTab(Math.max(0, i));
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const i = current;
        setIndex(i);
        break;
      }
    }
  };

  const defaultTabClass = ({
    active,
    disabled,
  }: {
    active: boolean;
    disabled: boolean;
  }) =>
    ['tab', active ? 'tab-active' : '', disabled ? 'tab-disabled' : ''].join(
      ' '
    );

  return (
    <div className={['tabs-container', className].join(' ')}>
      <div
        role='tablist'
        aria-label='Tabs'
        style={{ display: 'flex', justifyContent: 'space-between' }}
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab, i) => {
          const active = i === selectedIndex;
          const disabled = !!tab.disabled;
          const cls =
            (typeof tabClassName === 'function'
              ? tabClassName({ active, disabled })
              : tabClassName) || defaultTabClass({ active, disabled });

          return (
            <button
              key={tabIds[i]}
              id={tabIds[i]}
              role='tab'
              type='button'
              aria-selected={active}
              aria-controls={panelIds[i]}
              aria-disabled={disabled || undefined}
              tabIndex={active ? 0 : -1}
              className={cls}
              ref={(el) => (tabRefs.current[i] = el)}
              onClick={() => setIndex(i)}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <components.Loader />
      ) : (
        <div
          className='tab-panels'
          style={{ height: '68dvh', overflowY: 'auto' }}
        >
          {tabs.map((tab, i) => {
            const active = i === selectedIndex;
            return (
              <div
                key={panelIds[i]}
                id={panelIds[i]}
                role='tabpanel'
                aria-labelledby={tabIds[i]}
                hidden={!active}
                className={['tab-panel', panelClassName].join(' ')}
              >
                {active && tab.content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
